// PDF da Auditoria do Pedido (PO1-3241), gerado no navegador com jsPDF — mesmo
// molde do relatório de Emissões (Indicadores/Emissoes/relatorioPdf.js).
// Sem relatório narrativo gerado, o PDF sai só com os números do dossiê.
import { jsPDF } from "jspdf";
import { COR_TEXTO, COR_LABEL } from "views/Empresa/Indicadores/Emissoes/relatorioPdf";
import { fmtBRL, fmtData } from "views/Empresa/ConferenciaFatura/formatters";
import logoEverlog from "assets/img/logo.png";
import { rotuloIndicio, nomeArquivoPdf } from "./regras";

const MARGEM = 15;
// Paleta da marca Everlog (mesma das propostas comerciais).
const NAVY = [20, 57, 90]; // #14395a
const AZUL = [27, 135, 201]; // #1b87c9
const VERDE = [141, 198, 63]; // #8dc63f
const ALERTA = [178, 58, 43];
const LOGO_PROPORCAO = 3484 / 1023;

// Logo reduzido e convertido para JPEG no navegador: o PNG original tem ~450 KB.
let logoCache = null;
export function carregarLogo() {
  if (!logoCache) {
    logoCache = new Promise((resolve) => {
      if (typeof Image === "undefined" || typeof document === "undefined") return resolve(null);
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = 600;
          canvas.height = Math.round(600 / LOGO_PROPORCAO);
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.9));
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = logoEverlog;
    });
  }
  return logoCache;
}

function checarQuebra(doc, y, necessario) {
  const pageH = doc.internal.pageSize.getHeight();
  if (y + necessario > pageH - 16) {
    doc.addPage();
    return 20;
  }
  return y;
}

function titulo(doc, texto, y) {
  y = checarQuebra(doc, y, 14);
  doc.setFillColor(...VERDE);
  doc.rect(MARGEM, y - 3.6, 1.2, 4.6, "F");
  doc.setTextColor(...NAVY);
  doc.setFont(undefined, "bold");
  doc.setFontSize(12);
  doc.text(texto, MARGEM + 3.5, y);
  doc.setFont(undefined, "normal");
  return y + 7;
}

function paragrafo(doc, texto, y, { tamanho = 10, recuo = 0, marcador = null } = {}) {
  const largura = doc.internal.pageSize.getWidth() - 2 * MARGEM - recuo - (marcador ? 4 : 0);
  const linhas = doc.splitTextToSize(String(texto || ""), largura);
  doc.setFontSize(tamanho);
  doc.setTextColor(...COR_TEXTO);
  linhas.forEach((linha, i) => {
    y = checarQuebra(doc, y, 6);
    if (marcador && i === 0) doc.text(marcador, MARGEM + recuo, y);
    doc.text(linha, MARGEM + recuo + (marcador ? 4 : 0), y);
    y += tamanho * 0.5;
  });
  return y + 1.5;
}

function tabela(doc, y, colunas, linhas) {
  const pageW = doc.internal.pageSize.getWidth();
  y = checarQuebra(doc, y, 16);
  doc.setFillColor(...NAVY);
  doc.rect(MARGEM, y - 4, pageW - 2 * MARGEM, 6.5, "F");
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);
  colunas.forEach((c) => doc.text(c.label.toUpperCase(), c.x + (c.align ? 0 : 1), y, c.align ? { align: c.align } : undefined));
  y += 7;
  doc.setFontSize(8.5);
  doc.setTextColor(...COR_TEXTO);
  linhas.forEach((linha) => {
    y = checarQuebra(doc, y, 6);
    colunas.forEach((c, i) => {
      const v = linha[i] != null ? String(linha[i]) : "-";
      doc.text(v, c.x, y, c.align ? { align: c.align } : undefined);
    });
    y += 5.2;
  });
  return y + 3;
}

function cartoes(doc, y, itens) {
  const pageW = doc.internal.pageSize.getWidth();
  const gap = 3;
  const w = (pageW - 2 * MARGEM - gap * (itens.length - 1)) / itens.length;
  itens.forEach((it, i) => {
    const x = MARGEM + i * (w + gap);
    doc.setDrawColor(215, 222, 232);
    doc.setFillColor(246, 248, 251);
    doc.roundedRect(x, y, w, 17, 2, 2, "FD");
    doc.setFillColor(...(it.destaque || AZUL));
    doc.rect(x, y + 2, 1, 13, "F");
    doc.setFontSize(7.5);
    doc.setTextColor(...COR_LABEL);
    doc.text(it.label.toUpperCase(), x + 3, y + 5.5);
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.setTextColor(...(it.cor || COR_TEXTO));
    doc.text(it.valor, x + 3, y + 12.5);
    doc.setFont(undefined, "normal");
  });
  return y + 23;
}

export function montarPdfAuditoria(dossie, relatorio, { logo = null } = {}) {
  if (!dossie) throw new Error("Auditoria do pedido não carregada.");
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const t = dossie.totais || {};

  // Cabeçalho: logo à esquerda, título à direita, faixa azul + verde embaixo.
  if (logo) {
    doc.addImage(logo, "JPEG", MARGEM, 9, 16 * LOGO_PROPORCAO * 0.75, 12);
  } else {
    doc.setTextColor(...NAVY);
    doc.setFont(undefined, "bold");
    doc.setFontSize(18);
    doc.text("Everlog", MARGEM, 18);
    doc.setFont(undefined, "normal");
  }
  doc.setTextColor(...NAVY);
  doc.setFont(undefined, "bold");
  doc.setFontSize(15);
  doc.text(`Auditoria do pedido ${dossie.pedido}`, pageW - MARGEM, 15, { align: "right" });
  doc.setFont(undefined, "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...COR_LABEL);
  const cliente = dossie.embarcador && dossie.embarcador.nome ? ` · ${dossie.embarcador.nome}` : "";
  doc.text(`Auditoria de frete${cliente}`, pageW - MARGEM, 21, { align: "right" });
  doc.setFillColor(...AZUL);
  doc.rect(0, 27, pageW * 0.8, 1.6, "F");
  doc.setFillColor(...VERDE);
  doc.rect(pageW * 0.8, 27, pageW * 0.2, 1.6, "F");

  let y = 38;
  y = cartoes(doc, y, [
    { label: "Cotado", valor: t.cotado != null ? fmtBRL(t.cotado) : "Sem cotação" },
    { label: "Conferido pela tabela", valor: fmtBRL(t.conferido) },
    { label: "Cobrado", valor: fmtBRL(t.cobrado) },
    {
      label: "Cobrado - conferido",
      valor: fmtBRL(t.difCobradoConferido),
      cor: t.difCobradoConferido > 0.009 ? ALERTA : COR_TEXTO,
      destaque: t.difCobradoConferido > 0.009 ? ALERTA : VERDE,
    },
  ]);

  if (relatorio) {
    y = titulo(doc, "Resumo", y);
    y = paragrafo(doc, relatorio.resumo, y);
    if (relatorio.conferencia && relatorio.conferencia.length) {
      y = titulo(doc, "O que a conferência mostrou", y + 2);
      relatorio.conferencia.forEach((item) => {
        y = paragrafo(doc, item, y, { marcador: "•" });
      });
    }
  } else if (dossie.indicios && dossie.indicios.length) {
    y = titulo(doc, "O que a conferência mostrou", y);
    dossie.indicios.forEach((i) => {
      y = paragrafo(doc, `${rotuloIndicio(i.codigo)}: ${i.texto}`, y, { marcador: "•" });
    });
  }

  y = titulo(doc, "Documentos e faturas", y + 2);
  y = tabela(
    doc,
    y,
    [
      { label: "Documento", x: MARGEM },
      { label: "Emissão", x: 32 },
      { label: "Valor das NFs", x: 80, align: "right" },
      { label: "Cobrado", x: 104, align: "right" },
      { label: "Conferido", x: 128, align: "right" },
      { label: "Diferença", x: 150, align: "right" },
      { label: "Fatura", x: 154 },
      { label: "Vencimento", x: 172 },
    ],
    (dossie.ctes || []).map((c) => [
      c.numero,
      fmtData(c.emissao),
      fmtBRL(c.valorCarga),
      fmtBRL(c.cobrado),
      c.conferido != null ? fmtBRL(c.conferido) : "sem cálculo",
      c.diferenca != null ? fmtBRL(c.diferenca) : "-",
      c.fatura ? c.fatura.numero : "sem fatura",
      c.fatura ? fmtData(c.fatura.vencimento) : "-",
    ])
  );

  const outros = relatorio
    ? relatorio.outrosPontos || []
    : (dossie.pendencias || []).map((p) => p.texto);
  if (outros.length) {
    y = titulo(doc, "Outros pontos do pedido", y);
    outros.forEach((item) => {
      y = paragrafo(doc, item, y, { marcador: "•" });
    });
  }

  if (relatorio && relatorio.decisoes && relatorio.decisoes.length) {
    y = titulo(doc, "O que precisa de decisão", y + 2);
    const larguraTexto = doc.internal.pageSize.getWidth() - 2 * MARGEM - 4;
    relatorio.decisoes.forEach((d, i) => {
      // Pergunta e recomendação nunca se separam entre páginas.
      const linhas =
        doc.splitTextToSize(`${i + 1}. ${d.pergunta}`, larguraTexto).length +
        doc.splitTextToSize(`Recomendação: ${d.recomendacao}`, larguraTexto).length;
      y = checarQuebra(doc, y, linhas * 5 + 4);
      doc.setFont(undefined, "bold");
      y = paragrafo(doc, `${i + 1}. ${d.pergunta}`, y);
      doc.setFont(undefined, "normal");
      y = paragrafo(doc, `Recomendação: ${d.recomendacao}`, y, { recuo: 4 });
    });
  }

  const pageH = doc.internal.pageSize.getHeight();
  const total = doc.internal.getNumberOfPages();
  for (let p = 1; p <= total; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(215, 222, 232);
    doc.setLineWidth(0.2);
    doc.line(MARGEM, pageH - 12, pageW - MARGEM, pageH - 12);
    doc.setTextColor(...COR_LABEL);
    doc.setFontSize(8);
    doc.text(
      `Documento gerado em ${new Date().toLocaleString("pt-BR")} · Everlog Brasil · página ${p} de ${total}`,
      MARGEM,
      pageH - 8
    );
  }
  return doc;
}

export async function baixarPdfAuditoria(dossie, relatorio) {
  const logo = await carregarLogo();
  montarPdfAuditoria(dossie, relatorio, { logo }).save(nomeArquivoPdf(dossie.pedido));
}

// Arquivo para anexar na devolução da fatura (H5).
export async function arquivoPdfAuditoria(dossie, relatorio) {
  const logo = await carregarLogo();
  const blob = montarPdfAuditoria(dossie, relatorio, { logo }).output("blob");
  return new File([blob], nomeArquivoPdf(dossie.pedido), { type: "application/pdf" });
}
